import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]); // For tab counts
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [orgToArchive, setOrgToArchive] = useState(null);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [orgToRestore, setOrgToRestore] = useState(null);
  const [orgToApprove, setOrgToApprove] = useState(null);
  const [orgToReject, setOrgToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [requiresPayment, setRequiresPayment] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending'); // Start with pending to review new orgs
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    adminEmail: '',
  });

  useEffect(() => {
    fetchOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, showArchived]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all organizations for tab counts
      const allResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organizations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const allOrgs = allResponse.data.data?.organizations || [];
      setAllOrganizations(allOrgs);
      
      // Filter organizations based on status and active state
      let filteredOrgs = allOrgs;
      
      // Filter by status first
      if (statusFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org => org.status === statusFilter);
      }
      
      // Only apply active/archived filter for 'active' and 'all' tabs
      // Rejected and pending orgs should always show regardless of isActive
      if (statusFilter === 'all' || statusFilter === 'active') {
        filteredOrgs = filteredOrgs.filter(org => showArchived ? !org.isActive : org.isActive);
      }
      
      setOrganizations(filteredOrgs);
    } catch (error) {
      toast.error('Failed to load organizations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Organization created successfully!');
      setShowAddModal(false);
      setFormData({ name: '', slug: '', description: '', adminEmail: '' });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create organization');
    }
  };

  const handleArchiveClick = (org) => {
    setOrgToArchive(org);
    setShowArchiveModal(true);
  };

  const handleRestoreClick = (org) => {
    setOrgToRestore(org);
    setShowRestoreModal(true);
  };

  const handleDeleteClick = (org) => {
    setOrgToDelete(org);
    setShowDeleteModal(true);
  };

  const handleArchiveConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations/${orgToArchive.id}/archive`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Organization archived successfully!');
      setShowArchiveModal(false);
      setOrgToArchive(null);
      fetchOrganizations();
    } catch (error) {
      console.error('Archive organization error:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to archive organization';
      toast.error(errorMessage);
    }
  };

  const handleRestoreConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations/${orgToRestore.id}/restore`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Organization restored successfully!');
      setShowRestoreModal(false);
      setOrgToRestore(null);
      fetchOrganizations();
    } catch (error) {
      console.error('Restore organization error:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to restore organization';
      toast.error(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/organizations/${orgToDelete.id}?force=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Organization permanently deleted!');
      setShowDeleteModal(false);
      setOrgToDelete(null);
      fetchOrganizations();
    } catch (error) {
      console.error('Delete organization error:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete organization';
      toast.error(errorMessage);
    }
  };

  const handleApproveClick = async (org) => {
    try {
      const token = localStorage.getItem('token');
      // Fetch full organization details including admin email
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/organizations/${org.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOrgToApprove(response.data.data);
      setRequiresPayment(true); // Default to requiring payment
      setShowApproveModal(true);
    } catch (error) {
      toast.error('Failed to load organization details');
      console.error(error);
    }
  };

  const handleRejectClick = (org) => {
    setOrgToReject(org);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleApproveConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations/${orgToApprove.id}/approve`,
        { requiresPayment },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`Organization approved successfully! ${requiresPayment ? 'Payment required' : 'Free'} mode enabled.`);
      setShowApproveModal(false);
      setOrgToApprove(null);
      setRequiresPayment(true);
      fetchOrganizations();
    } catch (error) {
      console.error('Approve organization error:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to approve organization';
      toast.error(errorMessage);
    }
  };

  const handleRejectConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations/${orgToReject.id}/reject`,
        { reason: rejectionReason },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Organization rejected.');
      setShowRejectModal(false);
      setOrgToReject(null);
      setRejectionReason('');
      fetchOrganizations();
    } catch (error) {
      console.error('Reject organization error:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to reject organization';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Organizations Card */}
      <div className="card">
        {/* Card Header */}
        <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1.125rem', color: '#1f2937' }}>Organizations</h5>
            </div>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm ${showArchived ? 'btn-outline-secondary' : 'btn-secondary'}`}
                onClick={() => setShowArchived(!showArchived)}
                style={{
                  padding: '0.375rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
              >
                <i className={`bi ${showArchived ? 'bi-eye' : 'bi-archive'} me-1`}></i>
                {showArchived ? 'Show Active' : 'Show Archived'}
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setShowAddModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  color: 'white',
                  padding: '0.375rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderRadius: '0.375rem'
                }}
              >
                <i className="bi bi-plus me-1" style={{ fontSize: '0.875rem' }}></i>
                Add Organization
              </button>
            </div>
          </div>
          
          {/* Status Filter Tabs */}
          <ul className="nav nav-tabs" style={{ borderBottom: 'none' }}>
            {['all', 'pending', 'active', 'rejected'].map(status => {
              // Filter by active/archived first, then count by status
              const visibleOrgs = allOrganizations.filter(org => showArchived ? !org.isActive : org.isActive);
              const counts = {
                all: visibleOrgs.length,
                pending: visibleOrgs.filter(o => o.status === 'pending').length,
                active: visibleOrgs.filter(o => o.status === 'active' || !o.status).length,
                rejected: visibleOrgs.filter(o => o.status === 'rejected').length,
              };
              return (
                <li key={status} className="nav-item">
                  <button
                    className={`nav-link ${statusFilter === status ? 'active' : ''}`}
                    onClick={() => setStatusFilter(status)}
                    style={{
                      border: 'none',
                      borderBottom: statusFilter === status ? '2px solid #667eea' : '2px solid transparent',
                      color: statusFilter === status ? '#667eea' : '#6b7280',
                      fontWeight: statusFilter === status ? '600' : '400',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      background: 'none',
                      cursor: 'pointer',
                      textTransform: 'capitalize'
                    }}
                  >
                    {status} {statusFilter === status && `(${counts[status]})`}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Card Body */}
        <div className="card-body p-0">
          {organizations.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-building" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
              <p className="mt-3 mb-0" style={{ fontSize: '0.875rem' }}>No organizations found</p>
              <p className="text-muted" style={{ fontSize: '0.8125rem' }}>Click "Add Organization" to create your first organization</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <tr>
                    <th style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '1rem 1.5rem' }}>Name</th>
                    <th style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '1rem 1.5rem' }}>Contact</th>
                    <th style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '1rem 1.5rem' }}>Description</th>
                    <th style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '1rem 1.5rem' }}>Status</th>
                    <th style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '1rem 1.5rem' }}>Created</th>
                    <th style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '1rem 1.5rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '1rem 1.5rem' }}>
                        <div>
                          <strong style={{ fontSize: '0.875rem', color: '#1e293b', display: 'block' }}>{org.name}</strong>
                          <code style={{ fontSize: '0.75rem', backgroundColor: '#f1f5f9', padding: '0.2rem 0.4rem', borderRadius: '0.25rem', color: '#64748b' }}>
                            {org.slug}
                          </code>
                        </div>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                        <div style={{ fontSize: '0.875rem' }}>
                          {org.adminEmail && (
                            <div style={{ marginBottom: '0.25rem' }}>
                              <i className="bi bi-envelope" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                              <a href={`mailto:${org.adminEmail}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                                {org.adminEmail}
                              </a>
                            </div>
                          )}
                          {org.phoneNumber && (
                            <div>
                              <i className="bi bi-telephone" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                              <a href={`tel:${org.phoneNumber}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                                {org.phoneNumber}
                              </a>
                            </div>
                          )}
                          {!org.adminEmail && !org.phoneNumber && '-'}
                        </div>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: '#64748b' }}>{org.description || '-'}</td>
                      <td style={{ padding: '1rem 1.5rem' }}>
                        <span
                          className={`badge ${
                            org.status === 'pending' ? 'bg-warning' :
                            org.status === 'active' ? 'bg-success' :
                            'bg-danger'
                          }`}
                          style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', textTransform: 'capitalize' }}
                        >
                          {org.status || 'active'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                        {new Date(org.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem 1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {org.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => handleApproveClick(org)}
                                style={{
                                  color: '#10b981',
                                  fontSize: '1.125rem',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  transition: 'color 0.2s',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#059669'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#10b981'}
                                title="Approve"
                              >
                                <i className="bi bi-check-circle"></i>
                              </button>
                              <button
                                onClick={() => handleRejectClick(org)}
                                style={{
                                  color: '#dc3545',
                                  fontSize: '1.125rem',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  transition: 'color 0.2s',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#b02a37'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#dc3545'}
                                title="Reject"
                              >
                                <i className="bi bi-x-circle"></i>
                              </button>
                            </>
                          ) : (
                            <Link
                              to={`/super-admin/organizations/${org.id}`}
                              style={{ 
                                color: '#6b7280', 
                                fontSize: '1.125rem',
                                textDecoration: 'none',
                                transition: 'color 0.2s',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}
                          {org.isActive && org.status === 'active' ? (
                            <button
                              onClick={() => handleArchiveClick(org)}
                              style={{
                                color: '#f59e0b',
                                fontSize: '1.125rem',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                transition: 'color 0.2s',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#d97706'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#f59e0b'}
                              title="Archive"
                            >
                              <i className="bi bi-archive"></i>
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleRestoreClick(org)}
                                style={{
                                  color: '#10b981',
                                  fontSize: '1.125rem',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  transition: 'color 0.2s',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#059669'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#10b981'}
                                title="Restore"
                              >
                                <i className="bi bi-arrow-clockwise"></i>
                              </button>
                              <button
                                onClick={() => handleDeleteClick(org)}
                                style={{
                                  color: '#dc3545',
                                  fontSize: '1.125rem',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  transition: 'color 0.2s',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#b02a37'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#dc3545'}
                                title="Permanent Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Organization Modal */}
      {showAddModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Organization</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Organization Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Slug *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      placeholder="e.g. acme-corp"
                      required
                    />
                    <small className="text-muted">
                      URL-friendly identifier (lowercase, no spaces)
                    </small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      rows="3"
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Admin Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.adminEmail}
                      onChange={(e) =>
                        setFormData({ ...formData, adminEmail: e.target.value })
                      }
                      placeholder="admin@organization.com"
                      required
                    />
                    <small className="text-muted">
                      Assign an existing user as admin for this organization
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Organization
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        show={showArchiveModal}
        title="Archive Organization"
        message={`Are you sure you want to archive "${orgToArchive?.name}"? This will deactivate the organization but keep all data.`}
        onConfirm={handleArchiveConfirm}
        onCancel={() => {
          setShowArchiveModal(false);
          setOrgToArchive(null);
        }}
        confirmText="Archive"
        cancelText="Cancel"
      />

      {/* Restore Confirmation Modal */}
      <ConfirmModal
        show={showRestoreModal}
        title="Restore Organization"
        message={`Are you sure you want to restore "${orgToRestore?.name}"? This will reactivate the organization.`}
        onConfirm={handleRestoreConfirm}
        onCancel={() => {
          setShowRestoreModal(false);
          setOrgToRestore(null);
        }}
        confirmText="Restore"
        cancelText="Cancel"
      />

      {/* Permanent Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        title="⚠️ Permanent Delete Organization"
        message={`WARNING: Are you sure you want to PERMANENTLY delete "${orgToDelete?.name}"? This will delete ALL associated data including elections, users, votes, and transactions. This action CANNOT be undone!`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setOrgToDelete(null);
        }}
        confirmText="Permanently Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Approve Modal with Payment Toggle */}
      {showApproveModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Organization</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowApproveModal(false);
                    setOrgToApprove(null);
                    setRequiresPayment(true);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <h6 className="mb-3">Organization Details</h6>
                <div className="mb-3" style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '0.5rem' }}>
                  <div className="mb-2">
                    <strong style={{ fontSize: '0.875rem', color: '#64748b' }}>Name:</strong>
                    <div style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{orgToApprove?.name}</div>
                  </div>
                  <div className="mb-2">
                    <strong style={{ fontSize: '0.875rem', color: '#64748b' }}>Slug:</strong>
                    <div style={{ fontSize: '0.9375rem', color: '#1e293b' }}>
                      <code style={{ backgroundColor: '#e2e8f0', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>
                        {orgToApprove?.slug}
                      </code>
                    </div>
                  </div>
                  <div className="mb-2">
                    <strong style={{ fontSize: '0.875rem', color: '#64748b' }}>Description:</strong>
                    <div style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{orgToApprove?.description || '-'}</div>
                  </div>
                  <div className="mb-2">
                    <strong style={{ fontSize: '0.875rem', color: '#64748b' }}>Phone Number:</strong>
                    <div style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{orgToApprove?.phoneNumber || '-'}</div>
                  </div>
                  <div className="mb-0">
                    <strong style={{ fontSize: '0.875rem', color: '#64748b' }}>Admin Email:</strong>
                    <div style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{orgToApprove?.adminEmail || '-'}</div>
                  </div>
                </div>
                <p className="text-muted small mb-3">The organization admin will receive an email with a setup link to activate their account.</p>
                
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="requiresPayment"
                      checked={requiresPayment}
                      onChange={(e) => setRequiresPayment(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="requiresPayment">
                      <strong>Require Payment</strong>
                    </label>
                  </div>
                  <small className="text-muted">
                    {requiresPayment 
                      ? 'Organization must select and pay for pricing packages when creating elections.'
                      : 'Organization will have free access to all features (sponsored/partner organization).'}
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowApproveModal(false);
                    setOrgToApprove(null);
                    setRequiresPayment(true);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleApproveConfirm}
                >
                  Approve Organization
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject Organization</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRejectModal(false);
                    setOrgToReject(null);
                    setRejectionReason('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to reject "<strong>{orgToReject?.name}</strong>"?</p>
                <div className="mb-3">
                  <label className="form-label">Rejection Reason (optional)</label>
                  <textarea
                    className="form-control"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="3"
                    placeholder="Provide a reason for rejection..."
                  ></textarea>
                  <small className="text-muted">
                    This reason will be included in the rejection email sent to the organization admin.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRejectModal(false);
                    setOrgToReject(null);
                    setRejectionReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleRejectConfirm}
                >
                  Reject Organization
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations;
